import React from "react";

import {
  IconButton,
  TableCell,
  TableRow,
  Typography,
  Chip,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PendingIcon from "@mui/icons-material/Pending";
import ErrorIcon from "@mui/icons-material/Error";

import { Column } from "../../../../common/Table/types";
import { EpisodeStatus } from "../../../../Episode/enums/EpisodeStatus";

interface TableListProps {
  columns: Column[];
  row: Record<string, any>;
}

interface TableCellProps {
  column: Column;
  row: Record<string, any>;
  value: string;
}

function EpisodeCell({ column, row, value }: TableCellProps): JSX.Element {
  const handleEpisodeClick = (episodeId: string) => {
    window.location.href = `episodes/${episodeId}`;
  };

  if (column.id === "view" && row.status === EpisodeStatus.READY) {
    return (
      <TableCell key={column.id} align={column.align}>
        <IconButton onClick={() => handleEpisodeClick(row.id)}>
          <VisibilityIcon />
        </IconButton>
      </TableCell>
    );
  }
  if (column.id === "status") {
    const getStatusConfig = (status: string) => {
      switch (status) {
        case EpisodeStatus.READY:
          return {
            label: "Ready",
            icon: <CheckCircleIcon sx={{ fontSize: 16 }} />,
            color: "#10b981",
            bgColor: "#d1fae5",
            borderColor: "#34d399",
          };
        case EpisodeStatus.PENDING:
          return {
            label: "Pending",
            icon: <PendingIcon sx={{ fontSize: 16 }} />,
            color: "#ed6c02",
            bgColor: "#fff4e5",
            borderColor: "#ff9800",
          };
        case EpisodeStatus.FAILED:
          return {
            label: "Failed",
            icon: <ErrorIcon sx={{ fontSize: 16 }} />,
            color: "#d32f2f",
            bgColor: "#ffebee",
            borderColor: "#f44336",
          };
        default:
          return {
            label: status,
            icon: <PendingIcon sx={{ fontSize: 16 }} />,
            color: "#666666",
            bgColor: "#f5f5f5",
            borderColor: "#e0e0e0",
          };
      }
    };

    const statusConfig = getStatusConfig(value);

    return (
      <TableCell key={column.id} align={column.align}>
        <Chip
          label={statusConfig.label}
          size="small"
          variant="outlined"
          sx={{
            backgroundColor: statusConfig.bgColor,
            color: statusConfig.color,
            borderColor: statusConfig.borderColor,
            borderWidth: 1.5,
            fontWeight: 600,
            fontSize: "0.7rem",
            height: 24,
            minWidth: "auto",
            "& .MuiChip-label": {
              paddingLeft: 2,
              paddingRight: 2,
              fontSize: "0.7rem",
            },
            "&:hover": {
              backgroundColor: statusConfig.bgColor,
              opacity: 0.9,
            },
          }}
        />
      </TableCell>
    );
  }
  return (
    <TableCell key={column.id} align={column.align}>
      <Typography>
        {column.format && typeof value === "number"
          ? column.format(value)
          : value}
      </Typography>
    </TableCell>
  );
}

export default function TableList({ columns, row }: TableListProps) {
  return (
    <TableRow role="checkbox" tabIndex={-1}>
      {columns.map((column: Column) => {
        const value = row[column.id];
        return (
          <EpisodeCell
            key={column.id}
            column={column}
            row={row}
            value={value}
          />
        );
      })}
    </TableRow>
  );
}
